import Image from "next/image";
// A locale-less `/ej-health/…` href resolves to the DEFAULT locale, which
// every page in this vertical `notFound()`s — see `arrow-button.tsx`.
import { Link } from "@/i18n/routing";
import { EJHEALTH_COLORS, EJHEALTH_TYPE } from "@/lib/theme/ej-health";
import { AuthorChip } from "./author-chip";
import type { SidebarItem } from "@/fixtures/ej-health/fixtures";

interface Props {
  item: SidebarItem;
  variant?: "default" | "onDark";
  numbered?: number;
  /** Required. It defaulted to `"#"`, and no call site passed one — so every
   *  row in every sidebar was dead, and the `:not([href="#"])` guard in
   *  globals.css withheld the pointer affordance too. */
  href: string;
}

export function SidebarThumb({
  item,
  variant = "default",
  numbered,
  href,
}: Props) {
  const onDark = variant === "onDark";
  const titleColor = onDark ? EJHEALTH_COLORS.textOnDark : EJHEALTH_COLORS.dark;
  const metaColor = onDark ? EJHEALTH_COLORS.textOnDarkMuted : EJHEALTH_COLORS.textMuted;
  const showImage = numbered === undefined;

  return (
    <Link href={href} className="group hkej-tap flex items-start gap-4 py-4">
      {numbered !== undefined && (
        <span
          className="shrink-0"
          style={{ ...EJHEALTH_TYPE.numeral, color: EJHEALTH_COLORS.accent, minWidth: 56 }}
        >
          {String(numbered).padStart(2, "0")}
        </span>
      )}
      {showImage && (
        <div
          className="relative shrink-0 overflow-hidden"
          style={{
            width: 96,
            height: 54,
            backgroundColor: EJHEALTH_COLORS.surfaceAlt,
          }}
        >
          <Image
            src={item.img}
            alt={item.title}
            fill
            className="object-contain object-center transition-transform duration-500 group-hover:scale-[1.05]"
            sizes="96px"
            unoptimized
          />
        </div>
      )}
      <div className="flex flex-1 flex-col gap-1.5">
        <h4
          // A sidebar row. The home page's mobile size rule targets grid cards
          // only — see `[data-ejhealth-home]` in globals.css.
          data-keep-size
          className="hkej-tap-title line-clamp-2"
          style={{
            ...EJHEALTH_TYPE.cardTitleSm,
            color: titleColor,
            fontSize: 15,
            lineHeight: "22px",
          }}
        >
          {item.title}
        </h4>
        <div className="flex flex-col gap-1.5">
          {item.author && (
            <AuthorChip
              author={item.author}
              size={20}
              variant={onDark ? "onDark" : "default"}
            />
          )}
          <span style={{ ...EJHEALTH_TYPE.meta, color: metaColor }}>{item.time}</span>
        </div>
      </div>
    </Link>
  );
}
